/**
 * AboutHeadshot — terminal-framed avatar with identity cycling.
 *
 * • Cycles between the real photo and the Sung Jinwoo anime icon every 6 s.
 * • On each flip: a brief glitch burst plays via Framer Motion, then settles.
 * • Bottom status bar cycles: "IDENT: VERIFIED" → "IDENT: UNKNOWN" in sync.
 * • Hover triggers an immediate glitch + flip.
 * • Respects prefers-reduced-motion (no animation, shows real photo only).
 */

import { motion, useAnimation, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'

const REAL_SRC  = '/avatar.jpg'
const ANIME_SRC = '/Solo Leveling _ Sung Jinwoo _ Icon.jpeg'

const IDENTITIES = [
    { src: REAL_SRC,  label: 'IDENT: VERIFIED', color: '#00FF88' },
    { src: ANIME_SRC, label: 'IDENT: UNKNOWN',  color: '#FF3A6E' },
]

export default function AboutHeadshot({ className = '' }: { className?: string }) {
    const controls          = useAnimation()
    const shouldReduceMotion = useReducedMotion()
    const cooldownRef       = useRef(false)

    const [identIdx, setIdentIdx] = useState(0)
    const [imgSrc, setImgSrc]     = useState(REAL_SRC)
    const flipPendingRef          = useRef(false)

    const current = IDENTITIES[identIdx]
    const next    = IDENTITIES[(identIdx + 1) % IDENTITIES.length]

    /* ── glitch → flip ─────────────────────────────────────── */
    const doFlip = async () => {
        if (shouldReduceMotion) return
        if (cooldownRef.current || flipPendingRef.current) return
        cooldownRef.current    = true
        flipPendingRef.current = true

        // 1) glitch burst
        await controls.start({
            x:      [0, -3, 4, -2, 3, -1, 0],
            filter: [
                'grayscale(30%) contrast(1.1)',
                'hue-rotate(90deg)  saturate(2) contrast(1.6)',
                'hue-rotate(-90deg) saturate(2) contrast(1.6)',
                'hue-rotate(45deg)  saturate(1.5) contrast(1.3)',
                'grayscale(80%) contrast(1.8)',
                'grayscale(30%) contrast(1.1)',
            ],
            transition: {
                duration: 0.24,
                times:    [0, 0.15, 0.35, 0.55, 0.8, 1],
                ease:     'linear',
            },
        })

        // 2) swap image + identity
        const nextIdx = (identIdx + 1) % IDENTITIES.length
        setImgSrc(next.src)
        setIdentIdx(nextIdx)
        flipPendingRef.current = false

        // 3) settle
        await controls.start({
            x:      0,
            filter: 'grayscale(30%) contrast(1.1)',
            transition: { duration: 0 },
        })

        setTimeout(() => { cooldownRef.current = false }, 1200)
    }

    /* ── auto-cycle every 6 s ──────────────────────────────── */
    useEffect(() => {
        if (shouldReduceMotion) return
        const id = setInterval(doFlip, 6000)
        return () => clearInterval(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identIdx, shouldReduceMotion])

    return (
        <div className={`flex flex-col items-start ${className}`}>
            {/* File-path label */}
            <p className="text-[10px] font-mono text-[#444] mb-1 ml-1 tracking-wider">
                ~/assets/avatar.{identIdx === 0 ? 'jpg' : 'jpeg'}
            </p>

            {/* Terminal frame */}
            <div
                className="avatar-frame relative select-none"
                style={{
                    width:      200,
                    border:     '1px solid #222222',
                    padding:    '6px',
                    background: '#0D0D0D',
                    cursor:     shouldReduceMotion ? 'default' : 'pointer',
                }}
                onMouseEnter={doFlip}
                onFocus={doFlip}
            >
                {/* Main image */}
                <motion.img
                    key={imgSrc}
                    src={imgSrc}
                    alt="Rapheal Kwabena Asomani Gadoh"
                    animate={controls}
                    initial={{ filter: 'grayscale(30%) contrast(1.1)', x: 0 }}
                    style={{ display: 'block', width: '100%' }}
                    loading="eager"
                />

                {/* Scanline overlay */}
                <div
                    aria-hidden="true"
                    style={{
                        position:      'absolute',
                        inset:          6,
                        pointerEvents: 'none',
                        background:    `repeating-linear-gradient(
                            0deg,
                            rgba(0,255,136,0.04) 0px,
                            rgba(0,255,136,0.04) 1px,
                            transparent 1px,
                            transparent 2px
                        )`,
                    }}
                />

                {/* Bottom status bar — synced with identity */}
                <div
                    style={{
                        position:   'absolute',
                        bottom:      0,
                        left:        0,
                        right:       0,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)',
                        padding:    '4px 6px',
                    }}
                >
                    <motion.p
                        key={current.label}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="font-mono text-[8px] flex items-center gap-1"
                        style={{ color: current.color }}
                    >
                        <span
                            style={{
                                display:     'inline-block',
                                width:        5,
                                height:       5,
                                borderRadius: '50%',
                                background:   current.color,
                                boxShadow:   `0 0 4px ${current.color}`,
                            }}
                        />
                        {current.label}
                    </motion.p>
                </div>

                {/* Corner hex labels */}
                <span
                    className="font-mono text-[7px] text-primary/30 select-none"
                    style={{ position: 'absolute', top: 8, left: 8 }}
                >
                    R.KWABENA
                </span>
                <span
                    className="font-mono text-[7px] text-primary/30 select-none"
                    style={{ position: 'absolute', top: 8, right: 8 }}
                >
                    0x4F2A
                </span>
            </div>

            {/* Caption */}
            <p className="text-[9px] font-mono text-[#333] mt-1 ml-1 tracking-widest">
                SYS/{identIdx === 0 ? 'IDENT_VERIFIED' : 'IDENT_UNKNOWN'}
            </p>
        </div>
    )
}
